import { FunctionComponent, ReactElement, useEffect, useState } from "react";
import { Typography, Button, Paper, Box, FormControl, FormHelperText, InputLabel, Select, MenuItem, CircularProgress } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Check from '@material-ui/icons/CheckCircleOutline';
import SaveIcon from '@material-ui/icons/Save';
import { makeStyles } from '@material-ui/core/styles';
import * as Labels from '../../unit/labels';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Calendar from '@material-ui/icons/DateRange';
import OrderService from '../../services/order-service';
import WerkService, { WerkLookup } from '../../services/werk-service';
import { format } from "date-fns";
import axios from "axios";

const useStyles = makeStyles({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 210,
    paddingBottom: 20,
    alignItems: 'center',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 50,
    width: '100%',
  },
  field: { width: 300, maxWidth: '100%', marginTop: 10 },
  progress: { display: 'flex', flexGrow: 1, alignItems: 'center', padding: 20 },
});

interface CarJockeyFormProps {
  formCloseHandler: () => void;
  orderWerkId: string | null;
  orderId: string;
  vehicleId: string;
}

const errorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.errorMessage || fallback;
  }
  return fallback;
};

export const CarJockeyForm: FunctionComponent<CarJockeyFormProps> = ({ formCloseHandler, orderWerkId, orderId, vehicleId }): ReactElement => {
  const classes = useStyles();
  const [minDateTime] = useState(() => new Date(Date.now() + 30 * 60 * 1000));
  const [selectedDate, setSelectedDate] = useState<Date | null>(minDateTime);
  const [werks, setWerks] = useState<WerkLookup[]>([]);
  const [selectedWerkId, setSelectedWerkId] = useState('');
  const [werksLoading, setWerksLoading] = useState(true);
  const [werksError, setWerksError] = useState('');
  const [werksRetry, setWerksRetry] = useState(0);
  const [locations, setLocations] = useState<WerkLookup[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState('');
  const [locationsRetry, setLocationsRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setWerksLoading(true);
    setWerksError('');
    setSelectedWerkId('');
    setSelectedLocationId('');
    WerkService.getActiveWerks().then(items => {
      if (cancelled) return;
      setWerks(items);
      const orderWerk = items.find(item => item.id.toLowerCase() === orderWerkId?.toLowerCase());
      setSelectedWerkId(orderWerk?.id ?? '');
    }).catch(error => {
      if (!cancelled) setWerksError(errorMessage(error, 'Werke konnten nicht geladen werden.'));
    }).finally(() => {
      if (!cancelled) setWerksLoading(false);
    });
    return () => { cancelled = true; };
  }, [orderWerkId, werksRetry]);

  useEffect(() => {
    let cancelled = false;
    setLocations([]);
    setSelectedLocationId('');
    setLocationsError('');
    setLocationsLoading(!!selectedWerkId && !werksLoading);
    if (selectedWerkId && !werksLoading) {
      WerkService.getCarJockeyLocations(selectedWerkId).then(items => {
        if (!cancelled) setLocations(items);
      }).catch(error => {
        if (!cancelled) setLocationsError(errorMessage(error, 'Zielpunkte konnten nicht geladen werden.'));
      }).finally(() => {
        if (!cancelled) setLocationsLoading(false);
      });
    }
    // Ignore responses for a previously selected Werk or an unmounted form.
    return () => { cancelled = true; };
  }, [selectedWerkId, werksLoading, locationsRetry]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(false), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const hasActiveOrderWerk = werks.some(item => item.id.toLowerCase() === orderWerkId?.toLowerCase());
  const validDate = selectedDate !== null && !isNaN(selectedDate.getTime()) && selectedDate >= minDateTime;
  const canCreate = !werksLoading && !werksError && !locationsLoading && !locationsError &&
    !!selectedWerkId && locations.some(item => item.id === selectedLocationId) && validDate && !saving && !success;

  const handleCarJockey = async () => {
    if (!canCreate || !selectedDate) return;
    setSaving(true);
    setSaveError('');
    try {
      await OrderService.createCarJockey({
        werkId: selectedWerkId,
        destinationLocationId: selectedLocationId,
        planedDateTime: format(selectedDate, "yyyy-MM-dd'T'HH:mm:ss"),
        vehicleId,
        orderId,
      });
      setSuccess(true);
    } catch (error: unknown) {
      setSaveError(errorMessage(error, 'CarJockey konnte nicht erstellt werden. Bitte versuchen Sie es erneut.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper className={classes.paper}>
      <Box className={classes.toolbar}>
        <Button disabled={saving} color="secondary" onClick={formCloseHandler}>
          <CloseIcon style={{ marginRight: 5 }} />
          <Typography variant="subtitle1">{Labels.Close}</Typography>
        </Button>
        <Button disabled={!canCreate} color="primary" onClick={handleCarJockey}>
          <Typography style={{ marginRight: 5 }} variant="subtitle1">{Labels.Create}</Typography>
          <SaveIcon />
        </Button>
      </Box>
      {saving || success ? (
        <Box className={classes.progress}>
          {saving ? <CircularProgress size={50} /> : <Check style={{ color: 'green', fontSize: 50 }} />}
        </Box>
      ) : (
        <>
          <div className={classes.field}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileDateTimePicker
                value={selectedDate}
                onChange={setSelectedDate}
                renderInput={(props: any) => <TextField {...props} InputProps={{
                  endAdornment: <InputAdornment position="end"><Calendar /></InputAdornment>,
                }} />}
                ampm={false}
                label="Fälligkeit"
                disablePast
                inputFormat="dd/MM/yyyy HH:mm"
                minDateTime={minDateTime}
              />
            </LocalizationProvider>
          </div>
          {werksLoading ? <Box className={classes.progress}><CircularProgress size={24} /></Box> : werksError ? (
            <div className={classes.field}>
              <Typography color="error" role="alert">{werksError}</Typography>
              <Button onClick={() => setWerksRetry(value => value + 1)}>Erneut laden</Button>
            </div>
          ) : (
            <>
              {!hasActiveOrderWerk && (
                <FormControl className={classes.field} required>
                  <InputLabel id="werk-label" required={false}>Standort</InputLabel>
                  <Select labelId="werk-label" id="werk" value={selectedWerkId} onChange={event => {
                    setSelectedWerkId(event.target.value as string);
                    setSelectedLocationId('');
                    setLocations([]);
                    setLocationsLoading(true);
                    setSaveError('');
                  }} disabled={werks.length === 0}>
                    {werks.map(werk => <MenuItem key={werk.id} value={werk.id}>{werk.name}</MenuItem>)}
                  </Select>
                  <FormHelperText>
                    {werks.length === 0 ? 'Keine Standorte für CarJockey verfügbar.' :
                      orderWerkId ? 'Der Standort des Auftrags ist für CarJockey nicht verfügbar. Bitte wählen Sie einen verfügbaren Standort aus.' :
                        !selectedWerkId ? 'Bitte wählen Sie einen Standort aus.' : ''}
                  </FormHelperText>
                </FormControl>
              )}
              {!!selectedWerkId && (
                <FormControl className={classes.field} required error={!!locationsError}>
                  <InputLabel id="destination-label">Zielpunkt</InputLabel>
                  <Select labelId="destination-label" id="destination" value={selectedLocationId}
                    disabled={locationsLoading || !!locationsError || locations.length === 0}
                    onChange={event => { setSelectedLocationId(event.target.value as string); setSaveError(''); }}>
                    {locations.map(location => <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>)}
                  </Select>
                  {locationsLoading && <CircularProgress size={20} />}
                  <FormHelperText>
                    {locationsLoading ? 'Zielpunkte werden geladen…' : locationsError ||
                      (locations.length === 0 ? 'Keine aktiven Zielpunkte für dieses Werk verfügbar.' :
                        !selectedLocationId ? 'Bitte wählen Sie einen Zielpunkt aus.' : '')}
                  </FormHelperText>
                  {!!locationsError && <Button onClick={() => setLocationsRetry(value => value + 1)}>Erneut laden</Button>}
                </FormControl>
              )}
            </>
          )}
          {!!saveError && <Typography className={classes.field} color="error" role="alert">{saveError}</Typography>}
        </>
      )}
    </Paper>
  );
};
